import api from '@/utils/api.utils';

export interface DemonstratedInterestOpportunity {
  id: number;
  university_id: number;
  title: string;
  description: string;
  event_date: string;
  signup_link: string;
  event_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface OpportunityWithStatus extends DemonstratedInterestOpportunity {
  is_registered: boolean;
  is_completed: boolean;
  user_registration_id: number | null;
}

export interface UserRegistration {
  id: number;
  user_id: number;
  opportunity_id: number;
  registered_at: string;
  completed: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface RecommendationResponse {
  should_recommend: boolean;
  importance: string | null;
  recommended_count: number;
  opportunities: DemonstratedInterestOpportunity[];
  university_name?: string;
}

const BASE_URL = '/api/v1/demonstrated-interest';

export const getOpportunities = async (params?: {
  active_only?: boolean;
  upcoming_only?: boolean;
}): Promise<DemonstratedInterestOpportunity[]> => {
  const response = await api.get(`${BASE_URL}/opportunities`, { params });
  return response.data;
};

export const getOpportunitiesByUniversity = async (
  universityId: number,
  params?: {
    active_only?: boolean;
    upcoming_only?: boolean;
  }
): Promise<DemonstratedInterestOpportunity[]> => {
  const response = await api.get(
    `${BASE_URL}/opportunities/university/${universityId}`,
    { params }
  );
  return response.data;
};

export const getOpportunitiesWithStatus = async (params?: {
  university_id?: number;
  active_only?: boolean;
  upcoming_only?: boolean;
}): Promise<OpportunityWithStatus[]> => {
  const response = await api.get(`${BASE_URL}/opportunities-with-status`, {
    params,
  });
  return response.data;
};

export const getMyRegistrations = async (params?: {
  completed_only?: boolean;
}): Promise<UserRegistration[]> => {
  const response = await api.get(`${BASE_URL}/my-registrations`, { params });
  return response.data;
};

export const getMyRegistrationsForUniversity = async (
  universityId: number
): Promise<UserRegistration[]> => {
  const response = await api.get(
    `${BASE_URL}/my-registrations/university/${universityId}`
  );
  return response.data;
};

export const registerForOpportunity = async (data: {
  opportunity_id: number;
  completed?: boolean;
  notes?: string;
}): Promise<UserRegistration> => {
  const response = await api.post(`${BASE_URL}/register`, data);
  return response.data;
};

export const updateRegistration = async (
  registrationId: number,
  data: {
    completed?: boolean;
    notes?: string;
  }
): Promise<UserRegistration> => {
  const response = await api.put(`${BASE_URL}/registrations/${registrationId}`, data);
  return response.data;
};

export const unregister = async (registrationId: number): Promise<void> => {
  await api.delete(`${BASE_URL}/registrations/${registrationId}`);
};

export const getRecommendationsForUniversity = async (
  universityId: number
): Promise<RecommendationResponse> => {
  const response = await api.get(
    `${BASE_URL}/recommendations/university/${universityId}`
  );
  return response.data;
};
